/**
 * DeepResearchToolUI — the ChatGPT-style inline research card.
 *
 * Renders `start_deep_research` tool calls (DEEP_RESEARCH_REDESIGN.md):
 *  • launching → spinner line
 *  • running   → live panel (plan / researchers / steps / emerging findings)
 *  • finished  → stats header + report excerpt + buttons opening the full
 *                ResearchRunView in a sheet (Report / Findings tabs)
 *
 * The tool result is pure JSON: {"status":"started","run_id","research_id",
 * "thread_id"} — the card drives everything else from the run API (poll
 * while active) + the run's SSE events (via RunningProgressPanel).
 */

import { makeAssistantToolUI, useAssistantRuntime } from '@assistant-ui/react'
import {
  ArrowLeft,
  FileText,
  ListChecks,
  Loader2,
  RotateCcw,
  Telescope,
  TriangleAlert,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDocumentOverlayOptional } from '@/components/document-overlay/DocumentDetailOverlayContext'
import { SidebarEntityDetail } from '@/components/investigations/entity-detail/SidebarEntityDetail'
import { type ResearchRunTab, ResearchRunView } from '@/components/research/ResearchRunView'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useHypothesisRun } from '@/features/hypotheses/hooks/use-hypotheses'
import { useHypothesisFindings } from '@/features/hypotheses/hooks/use-hypothesis-findings'
import { RunningProgressPanel } from '@/features/hypotheses/RunningProgressPanel'
import { type HypothesisRun, isRunFinished } from '@/types/hypothesis'
import { plainSummaryExcerpt } from '@/utils/run-summary'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface DeepResearchArgs {
  brief?: string
}

interface LaunchIds {
  run_id: string
  research_id: string
}

function parseLaunch(result: unknown): { ids: LaunchIds | null; error: string | null } {
  if (result === undefined || result === null) return { ids: null, error: null }
  if (typeof result !== 'string') return { ids: null, error: null }
  try {
    const obj = JSON.parse(result) as Record<string, unknown>
    if (typeof obj.run_id === 'string' && typeof obj.research_id === 'string') {
      return { ids: { run_id: obj.run_id, research_id: obj.research_id }, error: null }
    }
    return { ids: null, error: result }
  } catch {
    // Non-JSON content is the tool's error message ("Failed to launch …").
    return { ids: null, error: result }
  }
}

function runDurationMinutes(run: HypothesisRun): number | null {
  if (!run.finishedAt) return null
  const ms = new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
  return Math.max(1, Math.round(ms / 60_000))
}

function DeepResearchRender({
  args,
  result,
  status,
}: {
  args: DeepResearchArgs
  result?: string
  status: Status
}) {
  const { t } = useTranslation()
  const runtime = useAssistantRuntime()
  // useParams, not useCaseContext — the chat host also mounts in the app
  // shell sidebar, outside CaseLayout (same pattern as RunCypherToolUI).
  const { caseId = '' } = useParams<{ caseId: string }>()
  const [sheetTab, setSheetTab] = useState<ResearchRunTab | null>(null)
  // Drill-in entity view inside the SAME sheet — stacking a second sheet on
  // top of the research sheet is a poor experience.
  const [entityId, setEntityId] = useState<string | null>(null)

  // The research sheet is a modal Radix dialog — it focus-traps the document
  // preview overlay rendered above it. Close the sheet when a source row
  // opens a document so the overlay is usable (same pattern as EvidenceCard's
  // built-in entity sheet).
  const docOverlay = useDocumentOverlayOptional()
  const docOverlayOpen = Boolean(docOverlay?.isOpen)
  useEffect(() => {
    if (docOverlayOpen && sheetTab !== null) {
      setSheetTab(null)
      setEntityId(null)
    }
  }, [docOverlayOpen, sheetTab])

  const { ids, error: launchError } = useMemo(() => parseLaunch(result), [result])

  const { data: run } = useHypothesisRun(caseId, ids?.research_id, ids?.run_id)
  const finished = !!run && isRunFinished(run.status)
  const { data: findings } = useHypothesisFindings(
    caseId,
    finished ? ids?.research_id : undefined,
    finished ? ids?.run_id : undefined
  )

  // Launch failed — the tool returned an error sentence instead of ids.
  if (launchError) {
    return (
      <Card
        size="compact"
        tone="destructive"
        className="flex flex-row items-start gap-2 p-3 text-xs"
      >
        <TriangleAlert className="size-3.5 shrink-0 text-destructive" />
        <span className="text-destructive">{launchError}</span>
      </Card>
    )
  }

  // Tool still executing (or ids not yet streamed).
  if (!ids || !run) {
    return (
      <Card
        size="compact"
        className="flex flex-row items-center gap-2 p-3 text-xs text-muted-foreground"
      >
        {status.type === 'incomplete' ? (
          <TriangleAlert className="size-3.5 shrink-0 text-destructive" />
        ) : (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
        )}
        <span>{t('researchRun.launching')}</span>
      </Card>
    )
  }

  // Active run — live panel (SSE-driven plan / researchers / findings).
  // Each card opens its own SSE connection; fine while a thread holds at
  // most a couple of active runs — multiplex per case if that changes.
  if (!finished) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
          <Telescope className="size-3.5 text-primary" />
          {t('researchRun.inProgress')}
        </div>
        {args.brief && (
          <p className="line-clamp-2 text-xs italic text-muted-foreground">{args.brief}</p>
        )}
        <RunningProgressPanel
          caseId={caseId}
          hypothesisId={ids.research_id}
          runId={ids.run_id}
          startedAt={run.startedAt}
        />
      </div>
    )
  }

  // Finished — stats + excerpt + expanders.
  const minutes = runDurationMinutes(run)
  const findingCount = findings?.length ?? 0
  const failed = run.status === 'finished_error'

  return (
    <>
      <Card tone={failed ? 'destructive' : 'primary'} className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2 text-xs font-medium">
          <Telescope className="size-3.5 shrink-0 text-primary" />
          <span className="text-foreground">
            {failed
              ? t('researchRun.failed')
              : t('researchRun.completed', { minutes: minutes ?? 1, count: findingCount })}
          </span>
        </div>

        {failed && run.errorMessage && (
          <p className="text-xs text-destructive">{run.errorMessage}</p>
        )}

        {run.summary && (
          <p className="line-clamp-4 whitespace-pre-line text-sm leading-relaxed text-foreground/90">
            {plainSummaryExcerpt(run.summary)}
          </p>
        )}

        <div className="flex flex-wrap gap-2 max-md:*:h-9">
          <Button size="sm" variant="default" onClick={() => setSheetTab('report')}>
            <FileText className="size-3.5" />
            {t('researchRun.openReport')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSheetTab('findings')}>
            <ListChecks className="size-3.5" />
            {t('researchRun.openFindings', { count: findingCount })}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              runtime.thread.composer.setText(
                t('researchRun.refinePrefill', { brief: args.brief ?? '' })
              )
            }
          >
            <RotateCcw className="size-3.5" />
            {t('researchRun.refine')}
          </Button>
        </div>
      </Card>

      <Sheet
        open={sheetTab !== null}
        onOpenChange={open => {
          if (!open) {
            setSheetTab(null)
            setEntityId(null)
          }
        }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader className="pr-12">
            <SheetTitle className="line-clamp-2 text-left">
              {args.brief ?? t('researchRun.inProgress')}
            </SheetTitle>
          </SheetHeader>
          {sheetTab !== null && entityId !== null && (
            <div className="flex flex-col gap-3 px-4 pb-6">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="self-start gap-1.5 max-md:h-9"
                onClick={() => setEntityId(null)}
              >
                <ArrowLeft className="size-3.5" />
                {t('researchRun.backToReport')}
              </Button>
              <SidebarEntityDetail
                caseId={caseId}
                nodeId={entityId}
                presentation={{ showHeader: true, graphActions: true }}
              />
            </div>
          )}
          {sheetTab !== null && entityId === null && (
            <div className="px-4 pb-6">
              <ResearchRunView
                caseId={caseId}
                run={run}
                findings={findings}
                initialTab={sheetTab}
                onOpenEntity={setEntityId}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}

export const DeepResearchToolUI = makeAssistantToolUI<DeepResearchArgs, string>({
  toolName: 'start_deep_research',
  render: DeepResearchRender,
})
